// Angular
import { Observable, map, takeUntil } from 'rxjs';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { KeyValue } from '@angular/common';

// Models
import { AppCleanup, LocationDto, LookUpResponse } from '../../../../shared/models';
import { CompanyListModel, CompanyViewModel } from '../../../company/models/company-view-model.model';
import { JobScopeDto } from '../../models/job-scope-dto.model';
import { UnitPeriodDto } from '../../models/unit-period-dto.model';
import { Job } from '../../models';
import { JobTypeDto } from '../../components/daily-report/models/job-type-dto';

// Libraries

import { Select } from '@ngxs/store';

// Services
import { LocationService } from '../../../../shared/services';
import { Timezone, TimezoneService } from '../../services/timezone.service';
import {
  assetsToJobInfoBopViewModels,
  bopControlToJobAssets,
  compareBopsByGuid,
  getBopDisplayName,
  JobAssetDto,
  JobDialogFormControlCache,
  JobDialogFormControls,
  JobDialogStreamFormControls,
  JobEditDialogOutputData,
  JobInfoDialogBopViewModel,
  JobInfoDialogInputData,
  JobInfoDialogService,
  jobTypeToBopTestType,
  validateAreBopObjects,
  validateIsBopObject,
} from '../job-info-dialog/job-info-dialog.service';

// Enums
import { BopTestType } from '../../models/bop-test-type.enum';

// Store
import { LookupState } from '../../../../shared/state/lookup.state';
import { UtilityHelper } from '../../../../shared/helpers';

@Component({
  selector: 'job-edit-dialog',
  templateUrl: './job-edit-dialog.component.html',
  styleUrls: ['job-edit-dialog.component.scss'],
})
export class JobEditDialogComponent extends AppCleanup implements OnInit, OnDestroy {
  componentTitle: string = 'Edit job information';
  @Select(LookupState.lookups) lookups$: Observable<LookUpResponse>;
  lookups: LookUpResponse;
  form: FormGroup<JobDialogFormControls>;
  jobScopes: JobScopeDto[] = [];
  jobTypes: JobTypeDto[] = [];
  locations: LocationDto[];
  showStreamOption: boolean = false;
  jobsToStreamTo: Job[] = [];
  jobChooseForm: FormGroup<JobDialogStreamFormControls>;
  timezones: Timezone[];
  currentDateTimeOffset = null;

  readonly bopTestType: BopTestType;
  readonly BopTestType = BopTestType;
  readonly initialBopViewModels: JobInfoDialogBopViewModel[];

  dataIsReady$: Observable<boolean>;
  jobTypes$: Observable<JobTypeDto[]>;
  companiesList$: Observable<CompanyListModel[]>;
  jobsToStreamTo$: Observable<Job[]>;
  allBops$: Observable<JobInfoDialogBopViewModel[]>;
  unitBops$: Observable<JobInfoDialogBopViewModel[]>;
  allJobScopes$: Observable<JobScopeDto[]>;
  controlCache: JobDialogFormControlCache;
  searchResultBops$: Observable<JobInfoDialogBopViewModel[]>;

  constructor(
    private timezoneService: TimezoneService,
    private locationService: LocationService,
    public dialogRef: MatDialogRef<JobEditDialogComponent>,
    private jobInfoDialogService: JobInfoDialogService,
    @Inject(MAT_DIALOG_DATA) public data: JobInfoDialogInputData
  ) {
    super();
    this.timezones = this.timezoneService.getDefaultTimezones();
    this.locations = this.locationService.locations;
    this.bopTestType = jobTypeToBopTestType(this.data.jobTypeId);
    this.initialBopViewModels = assetsToJobInfoBopViewModels(this.data.bops);

    this.controlCache = {
      streamToExistingJob: new FormControl(false),
      jobToStreamToId: new FormControl(null, [Validators.required]),

      facilityTestBop: new FormControl(this.initialBopViewModels[0], [Validators.required, validateIsBopObject]),
      fieldTestBops: new FormControl(this.initialBopViewModels, [validateAreBopObjects]),

      jobTypeId: new FormControl({ value: this.data.jobTypeId, disabled: true }, [Validators.required]),
      companyId: new FormControl(this.data.companyId),
      wellName: new FormControl(this.data.wellName, [Validators.maxLength(150)]),
      wellNumber: new FormControl(
        this.data.wellNumber,

        Validators.maxLength(50)
      ),
      scope: new FormControl(this.data.jobScopeId),
      locationId: new FormControl(this.data.locationId),
      dateStarted: new FormControl(
        this.displayTimeZone(this.data.dateStarted) || this.displayTimeZone(new Date()),
        Validators.required
      ),
      utcDateTimeOffset: new FormControl(this.data.utcDateTimeOffset ?? this.timezoneService.getCurrentTimeZone()),
      hide: new FormControl(this.data.hide),
    };
    const obs = this.jobInfoDialogService.createJobInfoDialogObservables(
      this.data.unitPeriods,
      this.controlCache.facilityTestBop
    );
    this.dataIsReady$ = obs.dataIsReady$;
    this.jobTypes$ = obs.jobTypes$;
    this.companiesList$ = obs.companiesList$.pipe(
      map((companies) => {
        // Hiding 'Other' company option only when it's not the current selection.
        let indexOfOther = companies.findIndex((company) => company.name === 'Other');
        if (indexOfOther >= 0 && companies[indexOfOther].id !== this.data.companyId) {
          if (indexOfOther) {
            companies.splice(indexOfOther, 1);
          }
        }

        return companies;
      })
    );
    this.jobsToStreamTo$ = obs.jobsToStreamTo$;
    this.allBops$ = obs.allBops$;
    this.unitBops$ = obs.unitBops$;
    this.allJobScopes$ = obs.allJobScopes$;
    this.searchResultBops$ = obs.searchResultBops$;
  }

  ngOnInit() {
    this.lookups$.pipe(takeUntil(this.destroy)).subscribe((lookups) => {
      if (lookups) {
        this.lookups = UtilityHelper.deepCopy(lookups);
        this.jobScopes = this.data.jobTypeId
          ? this.lookups.jobScopes.filter((js) => js.jobTypeId === this.data.jobTypeId)
          : [];
        this.jobTypes = this.lookups.jobTypes.filter((jt) => jt.id > 0);
      }
    });
    this.locations = this.locationService.locations;

    this.currentDateTimeOffset = this.data.utcDateTimeOffset;

    if (!this.isViewer) {
      this.form = new FormGroup<JobDialogFormControls>({
        jobTypeId: new FormControl({ value: this.data.jobTypeId, disabled: true }, Validators.required),
        companyId: new FormControl(this.data.companyId),
        wellName: new FormControl(this.data.wellName, [Validators.maxLength(150)]),
        wellNumber: new FormControl(this.data.wellNumber, Validators.maxLength(50)),
        scope: new FormControl(this.data.jobScopeId),
        locationId: new FormControl(this.data.locationId),
        hide: new FormControl(this.data.hide),
        dateStarted: new FormControl(this.displayTimeZone(this.data.dateStarted), Validators.required),
        utcDateTimeOffset: new FormControl(this.data.utcDateTimeOffset ?? this.timezoneService.getCurrentTimeZone()),

        ...(this.bopTestType === BopTestType.FacilityTest
          ? { bop: this.controlCache.facilityTestBop }
          : this.bopTestType === BopTestType.FieldTest
            ? { bop: this.controlCache.fieldTestBops }
            : {}),
      });

      this.jobInfoDialogService.handleLocationChanges(this.form.controls.locationId, this.destroy);
      this.handleJobTypeChange();
    } else {
      this.buildJobEditFormForLocalViewer();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  displayTimeZone(date: Date): Date {
    return this.timezoneService.timeZoneDate(
      date,
      this.data.utcDateTimeOffset || this.timezoneService.getCurrentTimeZone()
    );
  }

  getOffsetFromJobTimezone(timestampOrDate: any, jobOffset) {
    return this.timezoneService.getOffsetFromJobTimezone(timestampOrDate, jobOffset);
  }

  buildJobEditFormForLocalViewer() {
    this.jobChooseForm = null;

    const controls: JobDialogFormControls = {
      jobTypeId: new FormControl({ value: this.data.jobTypeId, disabled: true }, Validators.required),
      companyId: new FormControl(this.data.companyId || null),
      wellName: new FormControl(this.data.wellName || null, [Validators.maxLength(150)]),
      wellNumber: new FormControl(this.data.wellNumber || null, Validators.maxLength(50)),
      scope: new FormControl(this.data.jobScopeId || null),
      locationId: new FormControl(this.data.locationId),
      dateStarted: new FormControl(this.data.dateStarted, Validators.required),
      utcDateTimeOffset: new FormControl(this.data.utcDateTimeOffset ?? this.timezoneService.getCurrentTimeZone()),

      ...(this.bopTestType === BopTestType.FacilityTest
        ? { bop: this.controlCache.facilityTestBop }
        : this.bopTestType === BopTestType.FieldTest
          ? { bop: this.controlCache.fieldTestBops }
          : {}),
    };

    if (this.showStreamOption) {
      this.form = new FormGroup<JobDialogFormControls>({
        ...controls,
        streamToExistingJob: new FormControl(false),
      });

      this.handleExistingJobChangeForJobStartForm();
    } else {
      this.form = new FormGroup<JobDialogFormControls>({
        ...controls,
      });
    }

    this.jobInfoDialogService.handleLocationChanges(this.form.controls.locationId, this.destroy);
    this.handleJobTypeChange();
  }

  handleExistingJobChangeForJobStartForm() {
    this.form.controls.streamToExistingJob.valueChanges.pipe(takeUntil(this.destroy)).subscribe((valueChange) => {
      if (valueChange) {
        this.buildJobChooseFormForLocalViewer();
      }
    });
  }

  buildJobChooseFormForLocalViewer() {
    this.form = null;

    this.jobChooseForm = new FormGroup<JobDialogStreamFormControls>({
      streamToExistingJob: new FormControl(true),
      jobToStreamToId: new FormControl(null, Validators.required),
    });

    this.handleExistingJobChangeForJobChooseForm();
  }

  handleExistingJobChangeForJobChooseForm() {
    this.jobChooseForm.controls.streamToExistingJob.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe((valueChange) => {
        if (!valueChange) {
          this.buildJobEditFormForLocalViewer();
        }
      });
  }

  handleJobTypeChange() {
    this.form.controls.jobTypeId.valueChanges.pipe(takeUntil(this.destroy)).subscribe((valueChange) => {
      this.jobScopes = this.lookups.jobScopes.filter((js) => js.jobTypeId === valueChange);
    });
  }

  submit() {
    let offset = this.getOffsetFromJobTimezone(this.data.dateStarted, this.data.utcDateTimeOffset);
    let newDate = new Date(this.form.controls.dateStarted.value);
    newDate.setHours(newDate.getHours() + offset);

    let output: JobEditDialogOutputData;

    if (this.form && this.form.valid) {
      let timezoneChanged = this.currentDateTimeOffset != this.form.controls.utcDateTimeOffset.value;

      if (timezoneChanged === true) {
        this.timezoneService.changeTimezone({
          changed: timezoneChanged,
          offset: this.form.controls.utcDateTimeOffset.value,
        });
      }

      output = {
        action: 'edit-job',
        editJobInformationPayload: {
          key: this.data.key,
          name: this.data.name,
          jobTypeId: this.data.jobTypeId,
          jobAssets: bopControlToJobAssets(this.form.controls.bop, +this.data.key),
          companyId: this.form.controls.companyId.value,
          wellName: this.form.controls.wellName.value,
          wellNumber: this.form.controls.wellNumber.value,
          jobScopeId: this.form.controls.scope.value,
          location: this.form.controls.locationId.value !== 0 ? this.form.controls.locationId.value : null,
          dateStarted: newDate,
          jobStatusId: this.data.jobStatusId,
          utcDateTimeOffset: this.form.controls.utcDateTimeOffset.value,
          hide: this.form.controls.hide?.value ?? this.data.hide,
        },
        save: true,
      };
    } else if (this.jobChooseForm && this.jobChooseForm.valid) {
      output = {
        action: 'stream-job',
        jobToStreamToId: this.jobChooseForm.controls.jobToStreamToId.value,
        unitPeriodId: this.data.unitPeriods[0].id,
      };
    }

    this.dialogRef.close(output);
  }

  protected readonly getBopDisplayName = getBopDisplayName;
  protected readonly compareBopsByGuid = compareBopsByGuid;
}

export interface JobEditData {
  action: string;
  key: string;
  name: string;
  wellName?: string;
  wellNumber?: string;
  jobScopeId?: number;
  location?: LocationDto;
  hide?: boolean;
  locations?: Array<KeyValue<number, string>>;
  save?: boolean;
  companyId?: number;
  jobTypeId: number;
  company?: CompanyViewModel;
  jobStatusId: number;
  dateStarted: Date;
  utcDateTimeOffset?: number;
  unitPeriods: UnitPeriodDto[];
  unitPeriodId?: number;
  jobToStreamToId?: number;
  jobAssets?: JobAssetDto[];
}
